/**
 * UK-locale date / time formatters.
 *
 * The whole app uses these so the displayed format is consistent
 * (DD/MM/YYYY) regardless of the user's browser locale. Form data
 * still flows as ISO 8601 underneath — these are display only.
 *
 * Input types accepted: Date, ISO string, epoch milliseconds, or
 * nullish (returns empty string) so call sites don't have to
 * narrow before calling.
 */

const LOCALE = 'en-GB' as const;

function toDate(input: Date | string | number | null | undefined): Date | null {
  if (input === null || input === undefined || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 28/05/2026.
 * For dates only — no time component.
 */
export function formatDate(
  input: Date | string | number | null | undefined,
): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * 28/05/2026, 16:32. The default "show me when this happened" choice
 * for the app — used on flag/note/task timestamps, event timelines,
 * admin logs.
 */
export function formatDateTime(
  input: Date | string | number | null | undefined,
): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleString(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 16:32. Just the wall-clock time, no date. Used in Reception list
 * rows ("Arrived 09:14"), Register, Coordinator.
 */
export function formatTime(
  input: Date | string | number | null | undefined,
): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 28 May 2026. Longer, editorial-feel format — used in document
 * cards and report headers where the row has more breathing room.
 */
export function formatDateLong(
  input: Date | string | number | null | undefined,
): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * 28 May 2026, 16:32. Longer datetime — used in document cards
 * and email log rows.
 */
export function formatDateTimeLong(
  input: Date | string | number | null | undefined,
): string {
  const d = toDate(input);
  if (!d) return '';
  return d.toLocaleString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
